<script setup lang="ts">
// PROPS
import { type NuxtLinkProps } from '#app/components/nuxt-link';
import type { PropType } from 'vue';

export type BlockHeroItem = {
    to: NuxtLinkProps['to'];
    label: string;
    icon: string;
};

defineProps({
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    items: { type: Array as PropType<BlockHeroItem[]>, default: () => [] },
});
</script>

<template>
    <div class="relative">
        <div class="absolute top-0 left-0 z-0 ring-0">
            <slot name="cover">
                <div
                    class="relative top-1.5 left-1/2 mx-auto flex -translate-x-1/2 flex-nowrap justify-center gap-x-20 overflow-hidden sm:top-7 md:gap-x-44 xl:top-11 xl:gap-x-64"
                >
                    <img
                        alt=""
                        role="presentation"
                        src="/block/hero/yellow-bus.webp"
                        class="h-[175px] w-[663px] scale-x-[-1] object-cover object-left md:h-[341px] md:w-[1292px] xl:h-[438px] xl:w-[1660px]"
                    />
                    <img
                        alt=""
                        role="presentation"
                        src="/block/hero/blue-bus.webp"
                        class="h-[175px] w-[663px] object-cover object-left md:h-[341px] md:w-[1292px] xl:h-[438px] xl:w-[1660px]"
                    />
                </div>
            </slot>
        </div>
        <BaseWidthContainer class="relative z-10 mt-20 md:mt-40 xl:mt-56">
            <div class="bg-primary-500 text-inverted flex flex-col gap-y-8 rounded-xl p-4 pt-6 md:p-6 md:pt-8 xl:gap-y-12 xl:p-12 xl:pt-8">
                <div class="flex flex-col gap-y-8 md:gap-y-9 xl:gap-y-12">
                    <div class="flex flex-col gap-y-6 md:gap-y-7 xl:gap-y-8">
                        <h1 v-if="$slots.title || !!title" class="text-center text-3xl font-semibold md:text-4xl">
                            <slot name="title">{{ title }}</slot>
                        </h1>
                        <h2 v-if="$slots.subtitle || !!subtitle" class="text-center text-sm md:text-base">
                            <slot name="subtitle">{{ subtitle }}</slot>
                        </h2>
                    </div>
                    <div v-if="$slots.cta || items.length > 0" class="flex flex-row flex-wrap justify-center">
                        <slot name="cta">
                            <div class="grid w-full grid-cols-1 gap-0 overflow-hidden rounded-lg md:grid-cols-2 xl:grid-cols-4">
                                <UButton
                                    v-for="item in items"
                                    :key="item.label"
                                    :ui="{ base: 'rounded-none' }"
                                    :to="item.to"
                                    class="text-primary pl-12 text-lg font-medium md:text-xl xl:justify-center xl:pl-3.5"
                                    color="neutral"
                                    variant="outline"
                                    size="xl"
                                    :leading-icon="item.icon"
                                >
                                    {{ item.label }}
                                </UButton>
                            </div>
                        </slot>
                    </div>
                </div>
            </div>
        </BaseWidthContainer>
    </div>
</template>
